import { useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, getAuthHeaders, ApiError } from '../../lib/api'
import { 
  ArrowLeft, 
  Upload, 
  X,
  Loader2,
  AlertCircle,
  Calendar,
  Building2,
  BookOpen,
  Clock,
  FileText,
  ChevronDown,
  ChevronRight
} from 'lucide-react'

interface ProfessionWithCategories {
  userProfessionId: string
  professionName: string
  state: string
  categories: Array<{
    id: string
    name: string
    categoryType: string
    requiredHours: number | null
    hoursEarned: number
  }>
}

interface AllocationEntry {
  userProfessionId: string
  categoryId: string
  hoursAllocated: string
}

export default function AddCEPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const mode = searchParams.get('mode') || 'upload'
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<'upload' | 'form'>(mode === 'manual' ? 'form' : 'upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractError, setExtractError] = useState('')
  const [uploadedDocumentUri, setUploadedDocumentUri] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const [title, setTitle] = useState('')
  const [provider, setProvider] = useState('')
  const [hours, setHours] = useState<number>(0)
  const [completionDate, setCompletionDate] = useState('')

  const [allocations, setAllocations] = useState<AllocationEntry[]>([])
  const [expandedProfessions, setExpandedProfessions] = useState<Set<string>>(new Set())

  const { data: professionsWithCategories, isLoading: loadingProfessions } = useQuery<ProfessionWithCategories[]>({
    queryKey: ['/api/user/professions-with-categories'],
    queryFn: () => apiGet<ProfessionWithCategories[]>('/user/professions-with-categories'),
  })

  const maxHours = hours || 0

  const getProfessionTotal = (profId: string) => {
    return allocations
      .filter(a => a.userProfessionId === profId)
      .reduce((sum, a) => sum + (parseFloat(a.hoursAllocated) || 0), 0)
  }

  const getProfessionRemaining = (profId: string) => {
    return Math.max(0, maxHours - getProfessionTotal(profId))
  }

  const toggleProfession = (profId: string) => {
    setExpandedProfessions(prev => {
      const next = new Set(prev)
      if (next.has(profId)) {
        next.delete(profId)
      } else {
        next.add(profId)
      }
      return next
    })
  }

  const toggleCategory = (userProfessionId: string, categoryId: string) => {
    setAllocations(prev => {
      const exists = prev.find(a => a.userProfessionId === userProfessionId && a.categoryId === categoryId)
      if (exists) {
        return prev.filter(a => !(a.userProfessionId === userProfessionId && a.categoryId === categoryId))
      }
      const profTotal = prev
        .filter(a => a.userProfessionId === userProfessionId)
        .reduce((sum, a) => sum + (parseFloat(a.hoursAllocated) || 0), 0)
      const remaining = Math.max(0, maxHours - profTotal)
      const defaultHrs = remaining > 0 ? Math.min(remaining, maxHours).toString() : maxHours.toString()
      return [...prev, { userProfessionId, categoryId, hoursAllocated: defaultHrs }]
    })
  }

  const updateAllocationHours = (userProfessionId: string, categoryId: string, val: string) => {
    const cleaned = val.replace(/[^0-9.]/g, '')
    setAllocations(prev =>
      prev.map(a =>
        a.userProfessionId === userProfessionId && a.categoryId === categoryId
          ? { ...a, hoursAllocated: cleaned }
          : a
      )
    )
  }

  const saveMutation = useMutation({
    mutationFn: async (opts: { force?: boolean }) => {
      return apiPost<{ id: string }>('/continuing-education', {
        title,
        provider,
        hoursAwarded: hours,
        dateCompleted: completionDate || null,
        source: mode === 'upload' ? 'ai_extracted' : 'manual',
        documentUri: uploadedDocumentUri || undefined,
        allocations: allocations.length > 0 ? allocations : undefined,
        force: opts.force || undefined,
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/continuing-education'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/ce-progress-summary'] })
      if (data?.id) {
        navigate(`/ce/${data.id}`)
      } else {
        navigate('/ce')
      }
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.isDuplicate) {
        if (window.confirm(error.message)) {
          saveMutation.mutate({ force: true })
        }
      }
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const processFile = (file: File) => {
    setSelectedFile(file)
    setPreviewUrl(URL.createObjectURL(file))
    setExtractError('')
    handleExtract(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleExtract = async (file: File) => {
    setIsExtracting(true)
    setExtractError('')

    try {
      const formData = new FormData()
      formData.append('file', file)

      const uploadResponse = await fetch('/api/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document')
      }

      const uploadData = await uploadResponse.json()
      setUploadedDocumentUri(uploadData.url || uploadData.fileKey || null)

      const extractResponse = await fetch('/api/extract-ce', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ imageUrl: uploadData.url }),
      })

      if (!extractResponse.ok) {
        const errData = await extractResponse.json().catch(() => ({}))
        throw new Error(errData.message || 'Failed to extract data from document')
      }

      const responseData = await extractResponse.json()
      const data = responseData.extractedData || responseData
      
      setTitle(data.courseName || '')
      setProvider(data.provider || '')
      setHours(data.hoursAwarded || 0)
      setCompletionDate(data.completionDate || '')

      const matches = Array.isArray(data.professionMatches) ? data.professionMatches : []
      const extractedCategories = Array.isArray(data.categories) ? data.categories : []
      const ceHours = data.hoursAwarded || 0

      if (matches.length > 0) {
        const newAllocations: AllocationEntry[] = []
        const profIds = new Set<string>()

        const sortedMatches = [...matches]
          .filter((m: any) => m.userProfessionId && m.categoryId && m.confidence > 0.3)
          .sort((a: any, b: any) => (b.confidence || 0) - (a.confidence || 0))

        for (const match of sortedMatches) {
          const exists = newAllocations.find(
            a => a.userProfessionId === match.userProfessionId && a.categoryId === match.categoryId
          )
          if (!exists) {
            const profTotal = newAllocations
              .filter(a => a.userProfessionId === match.userProfessionId)
              .reduce((sum, a) => sum + (parseFloat(a.hoursAllocated) || 0), 0)
            const remaining = Math.max(0, ceHours - profTotal)

            if (remaining <= 0) continue

            const catHours = extractedCategories.find(
              (c: any) => c.name && match.categoryName &&
                c.name.toLowerCase() === match.categoryName.toLowerCase()
            )
            const allocHours = catHours ? Math.min(catHours.hours, remaining) : remaining

            newAllocations.push({
              userProfessionId: match.userProfessionId,
              categoryId: match.categoryId,
              hoursAllocated: allocHours.toString(),
            })
            profIds.add(match.userProfessionId)
          }
        }
        if (newAllocations.length > 0) {
          setAllocations(newAllocations)
          setExpandedProfessions(profIds)
        }
      }

      setStep('form')
    } catch (error) {
      setExtractError(error instanceof Error ? error.message : 'Failed to extract data')
    } finally {
      setIsExtracting(false)
    }
  }

  const handleSave = () => {
    if (!title || !hours) return

    if (maxHours > 0 && professionsWithCategories) {
      const overAllocatedProfs = (professionsWithCategories || []).filter(prof => {
        const profTotal = getProfessionTotal(prof.userProfessionId)
        return profTotal > maxHours
      })
      if (overAllocatedProfs.length > 0) {
        const names = overAllocatedProfs.map(p => p.professionName).join(', ')
        window.alert(`Hours exceed the CE total (${maxHours}) for: ${names}. Please adjust.`)
        return
      }
    }

    saveMutation.mutate({})
  }

  const hasProfessions = professionsWithCategories && professionsWithCategories.length > 0

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/ce')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {step === 'upload' ? 'Upload CE Certificate' : 'Add CE Record'}
        </h1>
      </div>

      {step === 'upload' ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {!selectedFile ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Upload your CE completion certificate
              </p>
              <p className="text-gray-500 mb-4">
                Drag and drop or click to select a file
              </p>
              <p className="text-sm text-gray-400">
                Supports JPG, PNG, PDF up to 10MB
              </p>
            </div>
          ) : extractError ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{extractError}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedFile(null)
                    setPreviewUrl(null)
                    setExtractError('')
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Try Again
                </button>
                <button
                  onClick={() => setStep('form')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Enter Manually
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              <p className="text-lg font-medium text-gray-900">Reading your certificate...</p>
              <p className="text-sm text-gray-500">This may take a few moments</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {loadingProfessions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Title *
                </label>
                <div className="relative">
                  <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Advanced Cardiac Life Support"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., American Heart Association"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CE Hours *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={hours}
                      onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Completion Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={completionDate}
                      onChange={(e) => setCompletionDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {hasProfessions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Categories</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Select which categories these hours count toward. You can split hours across multiple categories.
                  </p>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {professionsWithCategories!.map(prof => (
                      <div key={prof.userProfessionId}>
                        <button
                          type="button"
                          onClick={() => toggleProfession(prof.userProfessionId)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                        >
                          <div>
                            <span className="font-medium text-gray-900">{prof.professionName}</span>
                            <span className="text-sm text-gray-500 ml-2">({prof.state})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {allocations.filter(a => a.userProfessionId === prof.userProfessionId).length > 0 && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                getProfessionTotal(prof.userProfessionId) > maxHours && maxHours > 0
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {getProfessionTotal(prof.userProfessionId)}/{maxHours} hrs
                              </span>
                            )}
                            {expandedProfessions.has(prof.userProfessionId) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </button>
                        {expandedProfessions.has(prof.userProfessionId) && prof.categories.length > 0 && (
                          <div className="px-4 pb-3 space-y-2">
                            {prof.categories.map(cat => {
                              const isSelected = allocations.some(
                                a => a.userProfessionId === prof.userProfessionId && a.categoryId === cat.id
                              )
                              const alloc = allocations.find(
                                a => a.userProfessionId === prof.userProfessionId && a.categoryId === cat.id
                              )
                              return (
                                <div key={cat.id}>
                                  <label
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleCategory(prof.userProfessionId, cat.id)}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="flex-1">
                                      <span className="text-sm text-gray-700">{cat.name}</span>
                                      {cat.requiredHours != null && (
                                        <span className="text-xs text-gray-400 ml-2">
                                          ({cat.hoursEarned}/{cat.requiredHours} hrs)
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                  {isSelected && alloc && (
                                    <div className="flex items-center gap-2 ml-10 mt-1 mb-1">
                                      <span className="text-xs text-gray-500">Hours:</span>
                                      <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max={maxHours}
                                        value={alloc.hoursAllocated}
                                        onChange={e => updateAllocationHours(prof.userProfessionId, cat.id, e.target.value)}
                                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                      />
                                      <span className="text-xs text-gray-400">of {maxHours} hrs</span>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        {expandedProfessions.has(prof.userProfessionId) && prof.categories.length === 0 && (
                          <div className="px-4 pb-3">
                            <p className="text-sm text-gray-400">No hour-based categories</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {allocations.length > 0 && professionsWithCategories && (
                    <div className="mt-3 space-y-1">
                      {professionsWithCategories.map(prof => {
                        const profAllocCount = allocations.filter(a => a.userProfessionId === prof.userProfessionId).length
                        if (profAllocCount === 0) return null
                        const profTotal = getProfessionTotal(prof.userProfessionId)
                        const profRemaining = getProfessionRemaining(prof.userProfessionId)
                        const isOver = profTotal > maxHours && maxHours > 0
                        return (
                          <div
                            key={prof.userProfessionId}
                            className={`px-3 py-2 rounded-lg text-center text-sm font-medium ${
                              isOver ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                            }`}
                          >
                            {prof.professionName}: {profTotal} of {maxHours} hrs
                            {isOver ? (
                              <span className="text-xs text-red-500 ml-1">
                                (exceeds by {(profTotal - maxHours).toFixed(1)})
                              </span>
                            ) : profRemaining > 0 ? (
                              <span className="text-xs text-gray-500 ml-1">
                                ({profRemaining.toFixed(1)} remaining)
                              </span>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {!hasProfessions && !loadingProfessions && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    Add a renewal to track CE hours by category.{' '}
                    <button onClick={() => navigate('/professions')} className="underline font-medium">Add Renewal</button>
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => mode === 'manual' ? navigate('/ce') : setStep('upload')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  {mode === 'manual' ? 'Cancel' : 'Back'}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!title || !hours || saveMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save CE Record'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
